import { defineComponent as _defineComponent } from 'vue'
import type Vue from 'vue';
import { computed, defineAsyncComponent, getCurrentInstance, ref } from 'vue';

// Components
import BTicketAccessInfo from '../b-ticket-access-info/b-ticket-access-info.vue';
import BTicketBookingDetails from '../b-ticket-booking-details/b-ticket-booking-details.vue';
import BTicketDirectionsInformation from '../b-ticket-directions-information/b-ticket-directions-information.vue';
import BTicketHeader from '../b-ticket-header/b-ticket-header.vue';
import BTicketLocationInfo from '../b-ticket-location-info/b-ticket-location-info.vue';
import BTicketPolicies from '../b-ticket-policies/b-ticket-policies.vue';
import PrivateLoungeInfo from '../b-ticket-private-lounge-details/b-ticket-private-lounge-details.vue';
import BTicketTimeInfo from '../b-ticket-time-info/b-ticket-time-info.vue';
import BTicketTravelInsuranceContactInformation from '../b-ticket-travel-insurance-contact-information/b-ticket-travel-insurance-contact-information.vue';
import BTicketTravelInsuranceDocumentsAddons from '../b-ticket-travel-insurance-documents-addons/b-ticket-travel-insurance-documents-addons.vue';
import BTicketTravelInsuranceTravellerDetails from '../b-ticket-travel-insurance-traveller-details/b-ticket-travel-insurance-traveller-details.vue';
import BTicketTravelInsuranceTripDetails from '../b-ticket-travel-insurance-trip-details/b-ticket-travel-insurance-trip-details.vue';
import BTicketWrap from '../b-ticket-wrap/b-ticket-wrap.vue';
import Button from '../button/button.vue';
import NumSelector from '../num-selector/num-selector.vue';
import TicketPreloader from '../ticket-preloader/ticket-preloader.vue';
import BTicketWalletButtons from '../b-ticket-wallet-buttons/b-ticket-wallet-buttons.vue';
import type { GeoLocation } from '@white-label-types/location';
import type { Policy, PurchaseProduct, ReceiptProduct } from '@white-label-types/parking-booking';

// Constants
import { BARRIER_ACCESS_TYPES, FEATURE_FLAGS, PRODUCT_TYPES } from '@white-label-configuration/constants';

// Feature flags
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';

// Helpers
import { add, DATE_TIME_FORMATS, format } from '@white-label-helper/date-utilities';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { useContentPreloader, useScreenSize } from '@white-label-helper/shared-composables';
import { isPrivateLoungeParkingPartner, isPrivateLoungePartner } from '@white-label-helper/shared-helpers';

type Props = {
  ticketData: ReceiptProduct['items'][0] | PurchaseProduct['items'][0];
  hideOrderId?: boolean;
  orderReference?: string;
  hideAccessSection?: boolean;
  hideViewTicketsButton?: boolean;
  accessInfoFromEntranceInfo?: boolean;
  contentLoading?: boolean;
  subItems?: any[];
  hideLocationInfo?: boolean;
  isEntryExitProduct?: boolean;
  collapsible?: boolean;
  showDirections?: boolean;
  showPolicies?: boolean;
  bundleName?: string;
  extendedFlow?: boolean;
  /** Indicates this is the only ticket displayed on the receipt page */
  isSingleTicket?: boolean;
  walletPasses?: {
    apple?: { url: string };
    google?: { url: string };
  };
};


export default /*#__PURE__*/_defineComponent({
  __name: 'b-ticket',
  props: {
    ticketData: null,
    hideOrderId: { type: Boolean, default: false },
    orderReference: { default: '' },
    hideAccessSection: { type: Boolean, default: false },
    hideViewTicketsButton: { type: Boolean, default: false },
    accessInfoFromEntranceInfo: { type: Boolean, default: false },
    contentLoading: { type: Boolean, default: false },
    subItems: { default: () => [] },
    hideLocationInfo: { type: Boolean, default: undefined },
    isEntryExitProduct: { type: Boolean, default: false },
    collapsible: { type: Boolean, default: false },
    showDirections: { type: Boolean, default: false },
    showPolicies: { type: Boolean, default: false },
    bundleName: { default: '' },
    extendedFlow: { type: Boolean, default: false },
    isSingleTicket: { type: Boolean, default: false },
    walletPasses: { default: () => ({}) }
  },
  emits: ["toggle-details", "check-availability"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'toggle-details', isOpen: boolean): void;
  (e: 'check-availability', extendedExitDateTime: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { ticketData: ReceiptProduct['items'][0] | PurchaseProduct['items'][0], hideOrderId: boolean, orderReference: string, hideAccessSection: boolean, hideViewTicketsButton: boolean, accessInfoFromEntranceInfo: boolean, contentLoading: boolean, subItems: any[], hideLocationInfo: boolean, isEntryExitProduct: boolean, collapsible: boolean, showDirections: boolean, showPolicies: boolean, bundleName: string, extendedFlow: boolean, isSingleTicket: boolean, walletPasses: {
    apple?: { url: string };
    google?: { url: string };
  } };

const IconButton = defineAsyncComponent(() =>
  import('@white-label-icon/icon-button')
);


// Types
const proxy = getCurrentInstance()?.proxy as Vue;

// Props


// Emits


// State
const isOpen = ref(false);
const extendedDays = ref(1);
const minExtendedDays = ref(1);
const maxExtendedDays = ref(30);
const extensionDaysError = ref('');

// Computed

const productName = computed<string>(() => {
  let name = '';

  const data = props.ticketData;

  if ('parkingName' in data) {
    name = data.parkingName;
  } else if ('inventory_item' in data) {
    name = data.inventory_item?.display_name || '';
  }

  if (data.upgraded_from_order_item_id) {
    return `${name} (${proxy?.$t?.('UI.bookingStatuses.extended')})`;
  }

  return name;
});

const productOption = computed<string | undefined>(() => {
  //this produces 'undefined' on 355/1061 - not sure if its used on other partners and has different result
  return (props.ticketData as any)?.inventoryItem?.product_option?.code;
});


const isPrivateLounge = computed(() => productOption.value === 'lounge_private');

const isPrivateLoungeParking = computed(() => isPrivateLoungeParkingPartner());

const isPrivateLoungeOrParking = computed(() => {
  const isParkingProduct = props?.ticketData?.product_code === 'parking';
  if (!isParkingProduct) {
    return false;
  }

  return (isPrivateLoungePartner() && isParkingProduct) || isPrivateLoungeParking.value;
});

const showAccessInformation = computed(() => !isPrivateLoungeOrParking.value);

const isExtras = computed(() => (props.ticketData as any)?.product_code === PRODUCT_TYPES.EXTRAS);

const isTravelInsurance = computed(() => (props.ticketData as any)?.product_code === PRODUCT_TYPES.TRAVEL_INSURANCE);


const { isMobileResolution } = useScreenSize();

const hideOnMobileForExtras = computed(() => isMobileResolution.value && isExtras.value);

const shouldHideOrderId = computed(() => {
  return props.hideOrderId || isTravelInsurance.value;
});


const isAccessInfoExists = computed<boolean>(() => {
  const data = props.ticketData;

  return Boolean(
    (Array.isArray(data?.entry_methods) && data.entry_methods.length) ||
      (props.accessInfoFromEntranceInfo && data?.inventory_item?.entrance_info) ||
      (props.accessInfoFromEntranceInfo && data?.inventoryItem?.entrance_info)
  );
});

const showGroupsOnMobile = computed(() => subOptions.value?.length > 0 && isMobileResolution.value);

const accessInfo = computed<string>(() => {
  const data = props.ticketData;

  if ('inventory_item' in data) {
    return data?.inventory_item?.entrance_info ?? '';
  }

  return data?.inventoryItem?.entrance_info ?? '';
});

const locationInfo = computed<Pick<GeoLocation, 'latitude' | 'longitude'>>(() => {
  const data = props.ticketData;

  if ('inventory_item' in data) {
    return data.inventory_item.geo;
  }

  return data.inventoryItem.geo;
});

const currentEnvironment = computed<string | undefined>(() => {
  return process.env.NUXT_ENV_ENVIRONMENT;
});

const { contentPreloaderEnabled } = useContentPreloader();

const partnerId = computed<number>(() => getAppVariable('partner_id'));

const airportName = computed<string>(() => {
  // Temporary override of address for specific partner IDs and environments
  // to ensure consistent display in emails and receipts, and to prevent
  // customers from arriving at incorrect locations (e.g., airport vs. off-site parking).
  // Tickets: CUS-2756, IMP-1535, IMP-1641.
  const addressOverrides: Record<string, Record<number, string>> = {
    uat: {
      841: 'Target Park, 2053 Post Rd, Warwick, RI 02886',
      972: '',
      1006: '747 Air Terminal Pkwy, Melbourne, FL 32901',
      1020: 'Baymont by Wyndham JAX, 1153 Airport Rd, Jacksonville, FL 32218',
    },
    live: {
      120: 'Target Park, 2053 Post Rd, Warwick, RI 02886',
      266: '',
      275: '747 Air Terminal Pkwy, Melbourne, FL 32901',
      286: 'Baymont by Wyndham JAX, 1153 Airport Rd, Jacksonville, FL 32218',
    },
  };

  const envOverrides =
    currentEnvironment.value &&
    addressOverrides[currentEnvironment.value];

  const overrideAddress = envOverrides?.[partnerId.value];

  // Allow an empty string
  if (typeof overrideAddress === 'string') {
    return overrideAddress;
  }

  let name = '';
  let code = '';

  const data = props.ticketData;

  if ('locationInfo' in data) {
    name = data.locationInfo?.name;
    code = data.locationInfo?.code;
  } else if ('poi' in data) {
    name = data.poi?.name;
    code = data.poi?.code;
  }

  return `${name} (${code})`;
});

const exitDateTime = computed<string>(() => {
  const data = props.ticketData;

  return (
    data.exitDateTime ||
    data.criteria?.closed_at?.datetime ||
    data.product?.closed_at?.datetime ||
    ''
  );
});

const entryDateTime = computed<string>(() => {
  const data = props.ticketData;

  if (props.extendedFlow) {
    return exitDateTime.value;
  }

  return (
    data.entryDateTime ||
    data.criteria?.started_at?.datetime ||
    data.product?.started_at?.datetime ||
    ''
  );
});

const extendedExitDateTime = computed<string>(() => {
  return format(
    add(new Date(exitDateTime.value), { days: extendedDays.value }),
    DATE_TIME_FORMATS.year_month_day_UTC
  );
});

// Temporary fix
const subOptions = computed(() => {
  if (props.subItems?.length > 0) {
    return props.subItems;
  }

  if (props.ticketData.product_code === PRODUCT_TYPES.EXTRAS) {
    const title =
      props.ticketData.inventory_option?.sub_options?.[0]?.details?.name_formatted ?? '';
    return title ? [{ title }] : [];
  }

  return (
    props.ticketData.inventory_option?.sub_options?.map((product) => {
      const quantity = product.totals.quantity;
      const guests = proxy.$tc(
        `shared.customers.${product.details.name}`,
        quantity
      );

      return {
        title: `${quantity} x ${guests}`,
      };
    }) || []
  );
});

const cancellationPolicy = computed<Policy[]>(() => {
  const data = props.ticketData;

  if (data.cancellation_protection) {
    return data.cancellation_protection.cancellation_policies;
  }
  return data.inventory_option?.cancellation_policies || [];
});

const ticketIsExpanded = computed<boolean>(() => !props.collapsible || isOpen.value);

const isWalletPassSectionVisible = computed<boolean>(() => {
  const isAddToWalletFeatureEnabled = getFeatureFlag(FEATURE_FLAGS.ON_2105_ADD_TO_WALLET);
  const hasWalletPassLinks = Boolean(props.walletPasses.apple?.url || props.walletPasses.google?.url);
  return isAddToWalletFeatureEnabled && hasWalletPassLinks && props.isSingleTicket;
});

// Methods
function displayAccessSection(accessType: string): boolean {
  return (
    !props.hideAccessSection &&
    (accessType === BARRIER_ACCESS_TYPES.QRCODE || accessType === BARRIER_ACCESS_TYPES.PINCODE)
  );
}

function showHideDetails() {
  if (props.collapsible) {
    isOpen.value = !isOpen.value;
  }
  emit('toggle-details', isOpen.value);
}

function onDaysInput(days: number) {
  if (days < minExtendedDays.value || days > maxExtendedDays.value) {
    extensionDaysError.value = proxy.$t('bookingExtension.daysError', {
      minDays: minExtendedDays.value,
      maxDays: maxExtendedDays.value,
    }) as string;
    return;
  }

  extensionDaysError.value = '';
  extendedDays.value = days;
}

function checkExtendAvailability() {
  emit('check-availability', extendedExitDateTime.value);
}

expose({
  isOpen,
  extendedDays,
  extensionDaysError,
  airportName,
  entryDateTime,
  exitDateTime,
  extendedExitDateTime,
  isAccessInfoExists,
  isPrivateLounge,
  productName,
  ticketIsExpanded,
  displayAccessSection,
  showHideDetails,
  onDaysInput,
  checkExtendAvailability,
});

return { __sfc: true,IconButton, proxy, props, emit, isOpen, extendedDays, minExtendedDays, maxExtendedDays, extensionDaysError, productName, productOption, isPrivateLounge, isPrivateLoungeParking, isPrivateLoungeOrParking, showAccessInformation, isExtras, isTravelInsurance, isMobileResolution, hideOnMobileForExtras, shouldHideOrderId, isAccessInfoExists, showGroupsOnMobile, accessInfo, locationInfo, currentEnvironment, contentPreloaderEnabled, partnerId, airportName, exitDateTime, entryDateTime, extendedExitDateTime, subOptions, cancellationPolicy, ticketIsExpanded, isWalletPassSectionVisible, displayAccessSection, showHideDetails, onDaysInput, checkExtendAvailability, BTicketAccessInfo, BTicketBookingDetails, BTicketDirectionsInformation, BTicketHeader, BTicketLocationInfo, BTicketPolicies, PrivateLoungeInfo, BTicketTimeInfo, BTicketTravelInsuranceContactInformation, BTicketTravelInsuranceDocumentsAddons, BTicketTravelInsuranceTravellerDetails, BTicketTravelInsuranceTripDetails, BTicketWrap, Button, NumSelector, TicketPreloader, BTicketWalletButtons }
}

})